/**
 * ScheduledView
 *
 * View for scheduled (not yet sent) messages with send now / edit / cancel.
 */

import { Clock, X, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useScheduleSend } from '../hooks/useScheduleSend';
import type { EmailScheduledMessage } from '@/lib/api';

interface ScheduledViewProps {
  accountId: string | undefined;
}

function formatScheduleTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ScheduledView({ accountId }: ScheduledViewProps) {
  const { scheduled, loading, cancel } = useScheduleSend(accountId);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (scheduled.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-20">
        <CalendarClock className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-sm font-medium">No scheduled messages</p>
        <p className="text-xs mt-1">Schedule send from the compose window</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center px-4 py-3 border-b border-border">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <CalendarClock className="h-4 w-4" /> Scheduled
          <Badge variant="secondary" className="text-xs">
            {scheduled.length}
          </Badge>
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {scheduled.map((item: EmailScheduledMessage) => (
          <div
            key={item.id}
            className="flex items-center justify-between px-4 py-3 border-b border-border hover:bg-muted/50 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm">Message {item.messageId.substring(0, 8)}...</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <Clock className="h-3 w-3" />
                Scheduled for {formatScheduleTime(item.scheduledFor)}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs gap-1 text-destructive"
                onClick={() => cancel(item.id)}
              >
                <X className="h-3 w-3" /> Cancel
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
